import React, { useState } from "react";
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from "react-icons/hi";
import ButtonCustom from "../../../../features/ButtonCustom/ButtonCustom";
import "./style.scss";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek
} from "date-fns";
import { pl } from "date-fns/locale";
import { dayList } from "./Day";
import useDesktopSize from "../../../../utils/hooks/useDesktopSize";

interface IProps {
  onSelectDate: (value: Date) => void;
  getPrice?: (value: Date) => void;
  getPricePromotion?: (value: Date) => void;
  onChangeMonth?: (value: Date) => void;
  getPokazujDo?: (value: Date) => void;
  getSelectedDay?: (value: Date) => boolean;
  maxDate?: Date;
  minDate?: Date;
  getDisabledDay: (value: Date) => void;
}

const Calendar: React.FC<IProps> = ({
  onSelectDate,
  getPrice,
  getPricePromotion,
  onChangeMonth,
  getPokazujDo,
  getSelectedDay,
  maxDate,
  minDate,
  getDisabledDay
}: IProps) => {
  const isDesktop = useDesktopSize();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectDate, setSelectDate] = useState<Date | null>(null);
  const monthAndYear = format(currentDate, "LLLL yyyy", { locale: pl });
  let rows: Date[][] = [];
  const firstDayInMonth: Date = startOfMonth(currentDate);
  const endDayOfMonth: Date = endOfMonth(currentDate);
  const startDate: Date = startOfWeek(firstDayInMonth, { weekStartsOn: 1 }); // ustawiam pierwszy dzień tygodnia na poniedziałek
  const endDate: Date = endOfWeek(endDayOfMonth, { weekStartsOn: 1 });
  const days: Date[] = eachDayOfInterval({ start: startDate, end: endDate });

  while (days.length > 0) {
    rows.push(days.splice(0, 7));
  }

  // Usuń ostatni tydzień jeśli jest pusty
  const lastWeek: Date[] = rows[rows.length - 1];
  if (lastWeek) {
    if (lastWeek.length === 0 || lastWeek.every((day: Date) => !isSameMonth(day, currentDate))) {
      rows.pop();
    }
  }

  const getDayNumberIsSameMonth = (date: Date) => {
    if (isSameMonth(date, currentDate)) {
      return format(new Date(date), "d");
    } else {
      return "";
    }
  };

  const dayClass = (date: Date, week: number) => {
    let retValue = "";
    if (isSameMonth(date, currentDate)) {
      retValue += Boolean(getDisabledDay(date)) ? " day-class-disabled" : " day-class";

      if (week === rows.length) {
        retValue += " border-bottom";
        if (date.getDay() === endDayOfMonth.getDay()) {
          retValue += " border-right radius-bottom-right";
        }
        if (date.getDay() === 1) {
          retValue += " radius-bottom-left";
        }
      }

      if (date.getDay() === 0) {
        retValue += " border-right";
      }

      if (week === rows.length - 1 && (date.getDay() > endDayOfMonth.getDay() || date.getDay() === 0)) {
        retValue += " border-bottom";
      }

      if (date.getDay() !== 0 && date.getDay() < 6) {
        retValue += " background-white";
      } else {
        retValue += " background-error";
      }

      if (week === 1) {
        if (date.getDay() === firstDayInMonth.getDay()) {
          retValue += " radius-top-left";
        }
        if (date.getDay() === 0) {
          retValue += " radius-top-right";
        }
      }

      if (week === 2 && date.getDay() === 1 && firstDayInMonth.getDay() !== 1) {
        retValue += " radius-top-left";
      }

      if (week === rows.length - 1 && date.getDay() === 0 && endDayOfMonth.getDay() !== 0) {
        retValue += " radius-bottom-right";
      }
    }

    if (selectDate) {
      if (date.getTime() === selectDate.getTime()) {
        retValue += " select-date";
      }
    }
    if (getSelectedDay && getSelectedDay(date)) {
      retValue += " select-date";
    }

    return retValue;
  };

  const dayNumberClass = (date: Date) => (date.getDay() === 0 || date.getDay() === 6 ? "red-class" : "second-color");

  const onSelect = (date: Date) => {
    if (!Boolean(getDisabledDay(date))) {
      setSelectDate(date);
      onSelectDate(date);
    }
  };

  const handleChangeMonth = (amount: 1 | -1) => {
    const date = addMonths(currentDate, amount);
    if (!isMinMaxMonth(date, amount)) {
      setCurrentDate(date);
      if (onChangeMonth) {
        onChangeMonth(date);
      }
    }
  };

  const isMinMaxMonth = (currentDate: Date, amount: 1 | -1) => {
    if (amount === -1) {
      if (minDate) {
        return currentDate.getMonth() < minDate.getMonth();
      }
    }
    if (amount === 1) {
      if (maxDate) {
        return currentDate.getMonth() === maxDate.getMonth();
      }
    }

    return false;
  };

  // const isMaxMonth = (currentDate: Date) => {
  //   if (maxDate) {
  //     return currentDate.getMonth() === maxDate.getMonth();
  //   }
  //   if (minDate) {
  //     return currentDate.getMonth() < minDate.getMonth();
  //   }
  //
  //   return false;
  // };

  const pokazujDo = (date: Date) => {
    if (isDesktop) {
      if (getPokazujDo) {
        return getPokazujDo(date);
      }
    }
    return isDesktop;
  };

  // const isText = (date: Date) => {
  //   if (getExerciseGroupIds && !Boolean(getExerciseGroupIds(date))) {
  //     if (isPast(date) && isToday(date)) {
  //       return true;
  //     }
  //   }
  //   return false;
  // };

  return (
    <div className={"calendar-container"}>
      <div className={"calendar-header"}>
        <ButtonCustom
          title={""}
          className={`button-calendar-next-prev`}
          Icon={HiOutlineArrowNarrowLeft}
          iconPosition={"center"}
          onClick={() => handleChangeMonth(-1)}
        />
        <p>{monthAndYear}</p>
        <ButtonCustom
          title={""}
          className={`button-calendar-next-prev`}
          Icon={HiOutlineArrowNarrowRight}
          iconPosition={"center"}
          onClick={() => handleChangeMonth(1)}
        />
      </div>
      <div className={"calendar-divider"} />
      <div className={"calendar-day-container"}>
        {dayList.map((item: any, index: number) => (
          <div key={index} className={"day-wrapper"}>
            <p className={index > 4 ? "red-class" : "second-color"}>{item.shortName}</p>
          </div>
        ))}
      </div>
      <div className={"calendar-body"}>
        {rows.map((week: Date[], index: number) => (
          <div key={`${index}`} className={"calendar-week"}>
            {week.map((day: Date) => (
              <div
                key={`${day}`}
                className={`calendar-day ${dayClass(day, index + 1)}`}
                onClick={getDayNumberIsSameMonth(day) ? () => onSelect(day) : () => {}}
              >
                <p className={`day-number ${dayNumberClass(day)}`}>{getDayNumberIsSameMonth(day)}</p>
                {getDayNumberIsSameMonth(day) && (
                  <>
                    <p className={"day-price-promo"}>{getPrice && Boolean(getPrice(day)) ? `${getPrice(day)}` : ""}</p>
                    {getPrice && Boolean(getPrice(day)) ? (
                      <p className={"day-price"}>
                        {getPricePromotion && Boolean(getPricePromotion(day)) ? `${pokazujDo(day) ? "od" : ""}` : ""}{" "}
                        <span>
                          {getPricePromotion && Boolean(getPricePromotion(day)) ? `${getPricePromotion(day)}` : ""}
                        </span>
                      </p>
                    ) : (
                      <p className={"day-price"}>
                        {getPricePromotion && Boolean(getPricePromotion(day)) ? `${getPricePromotion(day)}` : ""}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Calendar;
